import type { ReactNode } from 'react'

export interface Column<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  /** Les nombres restent alignés à gauche même en arabe : sinon les chiffres se lisent mal. */
  numeric?: boolean
  /** Nom de tri accepté par le serveur. Absent : la colonne ne se trie pas. */
  sortField?: string
  /**
   * La colonne sort de l'affichage sous 640 px.
   *
   * À réserver à ce qui ÉCLAIRE la ligne sans l'identifier ni la décider : un code et
   * un statut restent, une date de création ou un libellé long s'effacent. Le contenu
   * n'est pas perdu — la fiche le porte —, et l'en-tête part avec la cellule, sinon le
   * tableau se désaligne.
   */
  secondary?: boolean
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  emptyLabel: string
  /** Tri courant, par exemple « name » ou « -name ». */
  sort?: string
  onSortChange?: (field: string) => void
}

function indicateur(sort: string | undefined, field: string | undefined): string {
  if (field === undefined || sort === undefined) return ''
  if (sort === field) return ' ▲'
  if (sort === `-${field}`) return ' ▼'
  return ''
}

export function DataTable<T>({
  columns, rows, rowKey, emptyLabel, sort, onSortChange,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <p className="text-texte-doux">{emptyLabel}</p>
  }

  return (
    <div className="overflow-x-auto rounded-app border border-bordure bg-carte">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column) => {
              const triable = column.sortField !== undefined && onSortChange !== undefined
              const classe =
                'whitespace-nowrap border-b border-bordure bg-vert-clair px-[0.8rem] py-[0.6rem] text-start font-semibold text-vert'
                + (column.secondary === true ? ' col-secondaire' : '')

              return (
                <th key={column.key} className={classe} aria-sort={
                  sort === column.sortField ? 'ascending'
                    : sort === `-${column.sortField}` ? 'descending'
                    : undefined
                }>
                  {triable ? (
                    <button
                      type="button"
                      className="font-semibold text-vert underline-offset-2 hover:underline"
                      onClick={() => onSortChange(column.sortField as string)}
                    >
                      {column.header}
                      {indicateur(sort, column.sortField)}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className="[&>tr:last-child>td]:border-b-0">
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={
                    (column.numeric
                      ? 'whitespace-nowrap border-b border-bordure px-[0.8rem] py-[0.6rem] text-end tabular-nums [direction:ltr] [unicode-bidi:isolate]'
                      : 'whitespace-nowrap border-b border-bordure px-[0.8rem] py-[0.6rem] text-start')
                    + (column.secondary === true ? ' col-secondaire' : '')
                  }
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
